import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  theme,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  IReleaseNote,
  ReleaseNoteAudience,
  ReleaseNotePublishStatus,
} from "../../interfaces/releaseNote.interface";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import {
  deleteReleaseNoteThunk,
  getReleaseNotesThunk,
  selectReleaseNotes,
  selectReleaseNotesStatus,
} from "./releaseNotesSlice";
import {
  AUDIENCE_LABELS,
  AUDIENCE_ORDER,
  audienceHasContent,
  audienceStatus,
  filterReleaseNotesByStatus,
  overallStatus,
  releaseNoteDisplayTitle,
} from "./releaseNotesHelpers";
import ReleaseNoteEditor from "./components/ReleaseNoteEditor/ReleaseNoteEditor";
import ShareModal from "./components/ShareModal/ShareModal";

const { Text } = Typography;

const ReleaseNotes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const releaseNotes = useAppSelector(selectReleaseNotes);
  const status = useAppSelector(selectReleaseNotesStatus);

  const [statusFilter, setStatusFilter] = useState<
    ReleaseNotePublishStatus | "all"
  >("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [shareNote, setShareNote] = useState<IReleaseNote | null>(null);

  const refresh = useCallback(
    () => dispatch(getReleaseNotesThunk({})),
    [dispatch]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openEditor = (id: string | null) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    refresh();
  };

  const audienceIcon = (note: IReleaseNote, audience: ReleaseNoteAudience) => {
    if (audienceStatus(note, audience) === ReleaseNotePublishStatus.Published) {
      return <CheckCircleTwoTone twoToneColor={token.colorSuccess} />;
    }
    if (audienceHasContent(note, audience)) {
      return <CheckCircleOutlined style={{ color: token.colorTextTertiary }} />;
    }
    return <MinusCircleOutlined style={{ color: token.colorTextQuaternary }} />;
  };

  const columns: ColumnsType<IReleaseNote> = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "releaseDate",
        key: "releaseDate",
        render: (value: string) => dayjs(value).format("DD MMM YYYY"),
      },
      {
        title: "Title",
        key: "title",
        render: (_, record) => {
          const title = releaseNoteDisplayTitle(record);
          return title ? (
            <Text strong>{title}</Text>
          ) : (
            <Text type="secondary">Untitled</Text>
          );
        },
      },
      {
        title: "Released Versions",
        dataIndex: "versions",
        key: "versions",
        render: (_, record) => (
          <Space size={[4, 4]} wrap>
            {record.versions.map((v) => (
              <Tag key={v}>{v}</Tag>
            ))}
          </Space>
        ),
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) =>
          overallStatus(record) === ReleaseNotePublishStatus.Published ? (
            <Tag color="success">Published</Tag>
          ) : (
            <Tag>Draft</Tag>
          ),
      },
      {
        title: "Target Audience",
        key: "audiences",
        render: (_, record) => (
          <Space size="middle" wrap>
            {AUDIENCE_ORDER.map((audience) => (
              <Space size={4} key={audience}>
                {audienceIcon(record, audience)}
                <Text type="secondary">{AUDIENCE_LABELS[audience]}</Text>
              </Space>
            ))}
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditor(record._id)}
            >
              Edit
            </Button>
            {overallStatus(record) === ReleaseNotePublishStatus.Published && (
              <Button
                size="small"
                icon={<ShareAltOutlined />}
                onClick={() => setShareNote(record)}
              >
                Share
              </Button>
            )}
            <Popconfirm
              title="Delete this release note?"
              onConfirm={() => dispatch(deleteReleaseNoteThunk(record._id))}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  const visibleNotes = useMemo(
    () => filterReleaseNotesByStatus(releaseNotes, statusFilter),
    [releaseNotes, statusFilter]
  );

  return (
    <div style={{ padding: token.paddingLG }}>
      <GeneralTableHeader
        title="Release Notes"
        avatarUrl={<FileTextOutlined />}
        actionButtons={[
          <Button
            key="new"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditor(null)}
          >
            New Release Note
          </Button>,
        ]}
      />

      <Card style={{ marginTop: token.margin }}>
        <Space style={{ marginBottom: token.margin }} wrap>
          <Select
            value={statusFilter}
            style={{ width: 180 }}
            onChange={setStatusFilter}
            options={[
              { label: "Status: All", value: "all" },
              { label: "Published", value: ReleaseNotePublishStatus.Published },
              { label: "Draft", value: ReleaseNotePublishStatus.Draft },
            ]}
          />
        </Space>

        <Table
          rowKey="_id"
          loading={status === "loading"}
          columns={columns}
          dataSource={visibleNotes}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </Card>

      <ReleaseNoteEditor
        open={editorOpen}
        releaseNoteId={editingId}
        onClose={closeEditor}
        onPublished={(note) => setShareNote(note)}
      />

      <ShareModal
        open={!!shareNote}
        releaseNote={shareNote}
        onClose={() => setShareNote(null)}
      />
    </div>
  );
};

export default ReleaseNotes;
